import { Injectable } from '@angular/core';

import { BetType } from '@bpos/common';
import { ParticipantMarketType } from '@cds/betting-offer';
import { FallbackMapping } from '@cds/betting-offer/domain-specific';
import { hasValue } from '@frontend/sports/common/core/utils/extended-types';
import { EventFactory } from '@frontend/sports/grid/core/feature/factories';
import { WinGameFixture, toWinGameFixture } from '@frontend/sports/racing/feature/models';
import { sortBy } from 'lodash-es';
import { firstValueFrom } from 'rxjs';

import { ParticipantPickType } from '../../../betslip-base/models/pick-models';
import { MyBetsRaceBetType } from '../../../my-bets-base/models/my-bets-response';
import { MyBetsBet, MyBetsBetSlip, MyBetsResultData } from '../../../my-bets-base/models/my-bets-viewmodels';
import { getPickType } from '../../services/mapping.helpers';
import { IReBetPicksPayload } from './re-bet.models';

@Injectable()
export class ReBetPicksService {
    constructor(private eventFactory: EventFactory) {}

    static getDefaultReBetPicks(): IReBetPicksPayload {
        return {
            winGameFixtures: [],
            xCastFixtures: [],
            fixtures: [],
        };
    }

    async createPicks(betslip: MyBetsBetSlip): Promise<IReBetPicksPayload> {
        const result = ReBetPicksService.getDefaultReBetPicks();
        let fixtures;
        if (!betslip.bets.find((bet) => bet.betBuilderInfo)) {
            fixtures = betslip.reBetPicks?.fixturePage?.fixtures;
        }

        if (!fixtures) {
            return Promise.resolve(result);
        }

        const bets = sortBy(betslip.bets, (bet) => bet.betIndex);

        const liveFallbacks = betslip.reBetPicks!.liveFallbacks;

        await firstValueFrom(
            this.eventFactory.create(fixtures.filter((f) => (f.games && f.games.length) || (f.optionMarkets && f.optionMarkets.length))),
        ).then((items) => (result.fixtures = items || []));

        const resultInfoBets = betslip.bets
            .filter((b) => b.resultInfo)
            .map((b) => this.getBetDetailsWithFallback(liveFallbacks, b.resultInfo))
            .filter(hasValue);

        result.fixtures.forEach((f) => {
            const v2Bet = betslip.bets.find((b) => f.id === b.optionMarketInfo?.event?.id || f.id === b.optionMarketInfo?.fixtureCompoundId);
            const resultBet = resultInfoBets.find((b) => b.fixtureId === f.id);

            f.optionGroups = f.optionGroups.filter((op) => op.id === v2Bet?.marketId.toString() || op.id === resultBet?.marketId.toString());

            if (!f.optionGroups.length) {
                return;
            }

            f.optionGroups[0].options = f.optionGroups[0].options.filter(
                (o) => o.id === resultBet?.optionId || o.id === v2Bet?.optionMarketInfo?.optionId,
            );
        });

        for (const bet of bets) {
            if (bet.participantInfo) {
                const participantsIds = Object.keys(bet.participantInfo.participantInfo);

                const fixture = fixtures.find((f) => f.id === bet.participantInfo!.fixtureCompoundId);

                if (!fixture) {
                    continue;
                }

                if (getPickType(bet.participantInfo.betType) === ParticipantPickType.Win || this.isXCast(bet.participantInfo.betType)) {
                    const pick = toWinGameFixture(fixture);
                    pick.participants = pick.participants.filter((p) => participantsIds.indexOf(p.id.toString()) > -1);

                    pick.participants.forEach((p) => (p.options = p.options.filter((o) => o.marketType === this.getMarketType(bet.betTypeName))));

                    this.setEachWay(bet, pick);

                    if (this.isXCast(bet.participantInfo.betType)) {
                        result.xCastFixtures.push(pick);
                    } else {
                        result.winGameFixtures.push(pick);
                    }
                }
            }
        }

        return result;
    }

    private setEachWay(bet: MyBetsBet, pick: WinGameFixture): void {
        if (bet.betTypeName.includes(BetType.EachWay)) {
            pick.participants[0].options[0].properties.isEachWay = true;
            const eachWay = bet.betTypeName.slice(BetType.EachWay.length + 1);

            pick.participants[0].options[0].properties.placeTerms = {
                numerator: +eachWay.split(' ')[0].split('/')[0],
                denominator: +eachWay.split(' ')[0].split('/')[1],
                places: +eachWay.slice(eachWay.length - 1),
            };
        }
    }

    private getBetDetailsWithFallback(
        liveFallbacks: { [key: number]: FallbackMapping },
        resultInfo: MyBetsResultData | null,
    ): { fixtureId: string; marketId: number; optionId: number } | null {
        if (!resultInfo) {
            return null;
        }
        const currentOptionId = resultInfo.optionId;
        const fixtureId = liveFallbacks[currentOptionId]?.fixtureId ?? resultInfo.event?.id ?? resultInfo.eventId.toString();
        const marketId = liveFallbacks[currentOptionId]?.gameId ?? resultInfo.marketId;
        const optionId = liveFallbacks[currentOptionId]?.resultId ?? resultInfo.optionId;

        return {
            fixtureId,
            marketId,
            optionId,
        };
    }

    private getMarketType(betTypeName: string): ParticipantMarketType {
        switch (betTypeName) {
            case ParticipantMarketType.Win:
                return ParticipantMarketType.Win;
            case ParticipantMarketType.Forecast:
                return ParticipantMarketType.Forecast;
            case ParticipantMarketType.Tricast:
                return ParticipantMarketType.Tricast;
            default:
                return ParticipantMarketType.Win;
        }
    }

    private isXCast(betType: MyBetsRaceBetType): boolean {
        const type = getPickType(betType);

        return (
            [
                ParticipantPickType.Tricast,
                ParticipantPickType.Forecast,
                ParticipantPickType.CombinationForecast,
                ParticipantPickType.CombinationTricast,
            ].indexOf(type) > -1
        );
    }
}
